import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateLoading } from "../../../redux/features/common/commonSlice";
import { showDangerToast, showToast } from "../../constants/toaster";
import ApiCaller from "../../constants/ApiCaller";
import urls from "../../constants/apiurl";

const useMerchantEdit = (merchantId, merchantDetailsData, onUpdateSuccess = null) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.userData);

  // Edit Mode States
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    agentCode: "",
    guarantorNumber: "",
    businessName: "",
    businessEmail: "",
    businessMobile: "",
    licenceNumber: "",
    businessCategory: [],
    displayAddress: "",
    description: "",
    businessDocument: "",
    businessLogo: "",
    businessBanner: "",
    liveSelfie: "",
    liveIntroVideo: ""
  });
  
  const [selectedFiles, setSelectedFiles] = useState({
    businessDocument: null,
    businessLogo: null,
    businessBanner: null,
    liveSelfie: null,
    liveIntroVideo: null
  });
  
  const [filePreviews, setFilePreviews] = useState({
    businessDocument: "",
    businessLogo: "",
    businessBanner: "",
    liveSelfie: "",
    liveIntroVideo: ""
  });

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9+\-\s()]+$/;
    return phoneRegex.test(phone);
  };

  const validateUrl = (url) => {
    const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
    return urlRegex.test(url);
  };

  // File upload function
  const uploadFileToServer = useCallback(async (file, fieldName) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      let path = urls.uploadImage;
      let res = await ApiCaller.post(formData, path, userData?.access_token, {
        'Content-Type': 'multipart/form-data'
      });
      
      const response = await res.response.json();
      
      if (response.status === 200 || response.status === "200") {
        const uploadedUrl = response?.data?.url || response?.data;
        return uploadedUrl;
      } else {
        throw new Error(response.message || "Failed to upload file");
      }
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  }, [userData?.access_token]);

  // Form validation
  const validateForm = useCallback(() => {
    // Required fields validation
    if (!editFormData.agentCode.trim()) {
      showDangerToast("Agent Code is required");
      return false;
    }
    if (!editFormData.guarantorNumber.trim()) {
      showDangerToast("Guarantor Number is required");
      return false;
    }
    if (!editFormData.businessName.trim()) {
      showDangerToast("Business Name is required");
      return false;
    }
    if (!editFormData.businessEmail.trim()) {
      showDangerToast("Business Email is required");
      return false;
    }
    
    // Email validation
    if (!validateEmail(editFormData.businessEmail)) {
      showDangerToast("Please enter a valid email address");
      return false;
    }
    
    // Phone number validation (if provided)
    if (editFormData.businessMobile && editFormData.businessMobile.trim()) {
      if (!validatePhone(editFormData.businessMobile.trim())) {
        showDangerToast("Please enter a valid phone number");
        return false;
      }
    }
    
    // URL validation for media files (if provided and not using file upload)
    const urlFields = [
      { field: 'businessDocument', name: 'Business Document' },
      { field: 'businessLogo', name: 'Business Logo' },
      { field: 'businessBanner', name: 'Business Banner' },
      { field: 'liveSelfie', name: 'Live Selfie' },
      { field: 'liveIntroVideo', name: 'Live Intro Video' }
    ];

    for (const { field, name } of urlFields) {
      if (editFormData[field] && editFormData[field].trim() && !selectedFiles[field] && !validateUrl(editFormData[field])) {
        showDangerToast(`Please enter a valid URL for ${name}`);
        return false;
      }
    }

    return true;
  }, [editFormData, selectedFiles]);

  // Reset edit state
  const resetEditState = useCallback(() => {
    setEditFormData({
      agentCode: "",
      guarantorNumber: "",
      businessName: "",
      businessEmail: "",
      businessMobile: "",
      licenceNumber: "",
      businessCategory: [],
      displayAddress: "",
      description: "",
      businessDocument: "",
      businessLogo: "",
      businessBanner: "",
      liveSelfie: "",
      liveIntroVideo: ""
    });
    setSelectedFiles({
      businessDocument: null,
      businessLogo: null,
      businessBanner: null,
      liveSelfie: null,
      liveIntroVideo: null
    });
    setFilePreviews({
      businessDocument: "",
      businessLogo: "",
      businessBanner: "",
      liveSelfie: "",
      liveIntroVideo: ""
    });
  }, []);

  // Initialize edit mode with current merchant data
  const initializeEditMode = useCallback(() => {
    const businessData = merchantDetailsData?.merchantBusinessDetailsList?.[0] || {};
    setEditFormData({
      agentCode: merchantDetailsData?.agentCode || "",
      guarantorNumber: merchantDetailsData?.guarantorNumber || "",
      businessName: businessData?.businessName || "",
      businessEmail: businessData?.businessEmail || "",
      businessMobile: businessData?.businessMobile || "",
      licenceNumber: businessData?.licenceNumber || "",
      businessCategory: businessData?.businessCategory || [],
      displayAddress: businessData?.displayAddress || "",
      description: businessData?.description || "",
      businessDocument: businessData?.businessDocument || "",
      businessLogo: businessData?.businessLogo || "",
      businessBanner: businessData?.businessBanner || "",
      liveSelfie: businessData?.liveSelfie || "",
      liveIntroVideo: businessData?.liveIntroVideo || ""
    });
    setIsEditMode(true);
  }, [merchantDetailsData]);

  // Update merchant data
  const updateMerchant = useCallback(async () => {
    try {
      dispatch(updateLoading(true));
      
      // Validate form
      if (!validateForm()) {
        dispatch(updateLoading(false));
        return false;
      }

      // Upload selected files and get their URLs
      const finalFormData = { ...editFormData };
      const fileFields = ['businessDocument', 'businessLogo', 'businessBanner', 'liveSelfie', 'liveIntroVideo'];
      
      for (const fieldName of fileFields) {
        if (selectedFiles[fieldName]) {
          try {
            const uploadedUrl = await uploadFileToServer(selectedFiles[fieldName], fieldName);
            finalFormData[fieldName] = uploadedUrl;
          } catch (error) {
            showDangerToast(`Failed to upload ${fieldName.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            dispatch(updateLoading(false));
            return false;
          }
        }
      }

      // Prepare update payload
      const updatePayload = {
        agentCode: finalFormData.agentCode,
        guarantorNumber: finalFormData.guarantorNumber,
        merchantBusinessDetailsList: [{
          ...merchantDetailsData?.merchantBusinessDetailsList?.[0],
          businessName: finalFormData.businessName,
          businessEmail: finalFormData.businessEmail,
          businessMobile: finalFormData.businessMobile,
          licenceNumber: finalFormData.licenceNumber,
          businessCategory: finalFormData.businessCategory,
          displayAddress: finalFormData.displayAddress,
          description: finalFormData.description,
          businessDocument: finalFormData.businessDocument,
          businessLogo: finalFormData.businessLogo,
          businessBanner: finalFormData.businessBanner,
          liveSelfie: finalFormData.liveSelfie,
          liveIntroVideo: finalFormData.liveIntroVideo
        }]
      };

      let path = urls.businessMerchantDetails + "/" + merchantId;
      let res = await ApiCaller.put(updatePayload, path, userData?.access_token);
      const response = await res.response.json();
      
      if (response.status === 200 || response.status === "200") {
        dispatch(updateLoading(false));
        setIsEditMode(false);
        showToast("Merchant details updated successfully");
        
        // Clear form and files after successful update
        resetEditState();
        
        // Call success callback if provided
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
        
        return true;
      } else {
        dispatch(updateLoading(false));
        showDangerToast(response.message || "Failed to update merchant details");
        return false;
      }
    } catch (error) {
      dispatch(updateLoading(false));
      console.error(error);
      showDangerToast(error.message || "Failed to update merchant details");
      return false;
    }
  }, [editFormData, dispatch, merchantId, userData?.access_token, merchantDetailsData, selectedFiles, uploadFileToServer, validateForm, onUpdateSuccess, resetEditState]);

  // Handle edit mode toggle
  const handleEditClick = useCallback(() => {
    initializeEditMode();
  }, [initializeEditMode]);

  const handleCancelEdit = useCallback(() => {
    setIsEditMode(false);
    resetEditState();
  }, [resetEditState]);

  const handleSaveEdit = useCallback(async () => {
    await updateMerchant();
  }, [updateMerchant]);

  // Handle form field changes
  const handleFormFieldChange = useCallback((field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Handle file selection
  const handleFileSelection = useCallback((file, fieldName) => {
    setSelectedFiles(prev => ({ ...prev, [fieldName]: file }));
    
    const previewUrl = URL.createObjectURL(file);
    setFilePreviews(prev => ({ ...prev, [fieldName]: previewUrl }));
    
    handleFormFieldChange(fieldName, "");
  }, [handleFormFieldChange]);

  // Handle file input changes
  const handleFileInputChange = useCallback((event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
      const isImage = fieldName !== 'liveIntroVideo';
      const isVideo = fieldName === 'liveIntroVideo';
      
      if (isImage && !file.type.startsWith('image/')) {
        showDangerToast("Please select an image file");
        return;
      }
      
      if (isVideo && !file.type.startsWith('video/')) {
        showDangerToast("Please select a video file");
        return;
      }
      
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        showDangerToast("File size should not exceed 10MB");
        return;
      }
      
      handleFileSelection(file, fieldName);
    }
    event.target.value = '';
  }, [handleFileSelection]);

  // Clear file selection
  const clearFileSelection = useCallback((fieldName) => {
    setSelectedFiles(prev => ({ ...prev, [fieldName]: null }));
    setFilePreviews(prev => ({ ...prev, [fieldName]: "" }));
  }, []);

  return {
    // State
    isEditMode,
    editFormData,
    selectedFiles,
    filePreviews,
    
    // Actions
    handleEditClick,
    handleCancelEdit,
    handleSaveEdit,
    handleFormFieldChange,
    handleFileInputChange,
    clearFileSelection,
    updateMerchant,
    
    // Utilities
    initializeEditMode,
    resetEditState,
    validateForm
  };
};

export default useMerchantEdit;